import React, { useRef, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonButton,
  IonContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonGrid,
} from '@ionic/react';
import { chevronForwardOutline, closeOutline, chatbubblesOutline, notificationsOutline } from 'ionicons/icons';
import styles from './InboxSettingsModal.module.scss';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import { useTranslation } from 'react-i18next';

export interface InboxSettingsModalProps {
  onClose: () => void;
  onOpenTemplates: () => void;
  onOpenInboxNotifications: () => void;
}

const InboxSettingsModal: React.FC<InboxSettingsModalProps> = ({ onClose, onOpenTemplates, onOpenInboxNotifications }) => {
  const { t } = useTranslation();
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const containerRef = useRef<HTMLIonGridElement>(null);

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

  return (
    <BasePageWithHeader
      ref={baseHeaderRef}
      title={t('components.InboxSettingsModal.inboxSettings')}
      toolbarClassName={styles.toolbar}
      smallTitleClassName={styles.smallTitle}
      titleHeaderClassName={styles.titleHeader}
      startButtons={
        <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
          <IonIcon icon={closeOutline} />
        </IonButton>
      }
      endButtons={
        <IonButton shape="round" className={styles.hiddenButton} style={{ visibility: 'hidden' }}>
          <IonIcon icon={closeOutline} />
        </IonButton>
      }
    >
      <IonContent scrollY={false} className={styles.pageContent}>
        <IonGrid
          ref={containerRef}
          className={styles.settingsContainer}
          onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
        >
          <TitleCard
            ref={titleCardAnimationRef}
            title={t('components.InboxSettingsModal.inboxSettings')}
            subtitle={t('components.InboxSettingsModal.customizeYourInboxToolsAndAlerts')}
            subtitleClassName={styles.heroSubtitle}
            cardClassName={styles.settingsTitleCard}
            titleContainerClassName={styles.titleContainer}
            titleClassName={styles.settingsTitle}
          />

          <div className={styles.settingsPanel}>
            <p className={styles.sectionEyebrow}>{t('components.InboxSettingsModal.quickActions')}</p>
            <IonList lines="none" className={styles.settingsList}>
              <IonItem
                button
                detail
                detailIcon={chevronForwardOutline}
                lines="none"
                className={styles.settingAction}
                onClick={() => {
                  onClose();
                  onOpenTemplates();
                }}
              >
                <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                  <IonIcon icon={chatbubblesOutline} />
                </div>
                <IonLabel className={styles.actionLabel}>
                  <h2>{t('components.InboxSettingsModal.savedReplies')}</h2>
                  <p>{t('components.InboxSettingsModal.manageSavedReplies')}</p>
                </IonLabel>
              </IonItem>

              <IonItem
                button
                detail
                detailIcon={chevronForwardOutline}
                lines="none"
                className={styles.settingAction}
                onClick={() => {
                  onClose();
                  onOpenInboxNotifications();
                }}
              >
                <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                  <IonIcon icon={notificationsOutline} />
                </div>
                <IonLabel className={styles.actionLabel}>
                  <h2>{t('components.InboxSettingsModal.inboxNotifications')}</h2>
                  <p>{t('components.InboxSettingsModal.manageNotificationSettings')}</p>
                </IonLabel>
              </IonItem>
            </IonList>
          </div>
          <div className={styles.scrollSpacer} />
        </IonGrid>
      </IonContent>
    </BasePageWithHeader>
  );
};

export default InboxSettingsModal;
